import { useState, useMemo, useEffect, lazy, Suspense } from "react";
import { useSEO } from "@/hooks/use-seo";
import { ArrowRight, Search, ChevronDown, ChevronUp, Star, Shield, Smartphone, CreditCard, Clock, Award, BookOpen, HelpCircle, Scale, Lock, Wallet, AlertTriangle, Calendar, Trophy, Globe, Gamepad2, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CASINOS, PAYMENT_METHOD_NAMES, type CasinoData } from "@/lib/casinoData";
const PaymentLogoPill = lazy(() =>
  import("@/components/PaymentLogoPill").then(m => ({ default: m.PaymentLogoPill }))
);

const CRYPTO_METHODS = ["bitcoin", "ethereum", "litecoin", "ripple", "tether", "solana", "bnb", "bitcoin-cash", "dogecoin", "cardano", "usd-coin", "tron"];

function hasCrypto(casino: CasinoData) {
  return casino.paymentMethods.some(m => CRYPTO_METHODS.includes(m));
}

type SortOption = "rating" | "bonusValue" | "name";
type LicenseFilter = "all" | "MGA" | "Curaçao" | "Anjouan";
type DepositFilter = "all" | "250" | "500";

const FAQ_ITEMS = [
  {
    q: "Jak vybrat nejlepší online kasino?",
    a: "Při výběru online kasina se zaměřte na platnou licenci od uznávaného regulátora, šíři nabídky her, podmínky bonusů (zejména wagering), dostupné platební metody, rychlost výběrů a kvalitu zákaznické podpory. Naše recenze vám pomohou porovnat všechny tyto faktory.",
  },
  {
    q: "Jsou online kasina v Česku bezpečná?",
    a: "Kasina s platnou licencí od regulátorů jako MGA, Curaçao eGaming nebo Anjouan Gaming musí splňovat přísné bezpečnostní standardy. Všechna kasina v našem seznamu používají SSL šifrování a prochází pravidelnými audity. Vždy si ověřte platnost licence před registrací.",
  },
  {
    q: "Co znamená wagering (protočení) u bonusu?",
    a: "Wagering je podmínka, kolikrát musíte protočit bonusovou částku, než si můžete výhry vybrat. Například wagering 35× u bonusu 1 000 Kč znamená, že musíte vsadit celkem 35 000 Kč. Nižší wagering je pro hráče výhodnější.",
  },
  {
    q: "Jaké platební metody mohu použít?",
    a: "Většina kasin v našem seznamu podporuje platební karty (Visa, Mastercard), e-peněženky (Skrill, Neteller), kryptoměny (Bitcoin, Ethereum) a předplacené karty (Paysafecard). Konkrétní dostupné metody se liší podle kasina.",
  },
  {
    q: "Jak rychle mohu vybrat výhry?",
    a: "Doba výběru závisí na kasinu a zvolené platební metodě. E-peněženky a kryptoměny nabízejí nejrychlejší výběry (obvykle do 24 hodin). Bankovní převody a platební karty mohou trvat 2–5 pracovních dní. Při prvním výběru může být vyžadována KYC verifikace.",
  },
  {
    q: "Mohu hrát v online kasinu z mobilního telefonu?",
    a: "Ano, většina moderních online kasin je plně optimalizována pro mobilní zařízení. Mnohá kasina nabízejí také dedikované mobilní aplikace pro Android a iOS. V našem seznamu najdete informaci o mobilní podpoře u každého kasina.",
  },
  {
    q: "Jaký je rozdíl mezi licencemi MGA, Curaçao a Anjouan?",
    a: "MGA (Malta Gaming Authority) je nejprestižnější evropská licence s nejvyšší úrovní ochrany hráčů. Curaçao eGaming je nejrozšířenější mezinárodní licence se střední úrovní regulace. Anjouan Gaming je novější jurisdikce se základními požadavky na provozovatele.",
  },
  {
    q: "Musím platit daně z výher v online kasinu?",
    a: "V České republice jsou výhry z hazardních her osvobozeny od daně z příjmu, pokud nepřesáhnou 1 000 000 Kč za zdaňovací období. Výhry nad tento limit podléhají 15% dani. Doporučujeme konzultovat aktuální daňovou legislativu s odborníkem.",
  },
];

function FaqItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-gray-100 bg-white hover-elevate overflow-visible" data-testid={`faq-kasina-item-${index}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 p-5 text-left"
        data-testid={`faq-kasina-toggle-${index}`}
      >
        <span className="font-semibold text-gray-900 text-sm leading-normal pr-2">{q}</span>
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-50 flex items-center justify-center text-gray-400">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-48 opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="px-5 pb-5">
          <div className="w-full h-px bg-gray-100 mb-3" />
          <p className="text-gray-600 text-sm leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

function CasinoCard({ casino }: { casino: CasinoData }) {
  const [showPayments, setShowPayments] = useState(false);
  const cryptoSupport = hasCrypto(casino);

  return (
    <div
      className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
      data-testid={`casino-card-${casino.id}`}
    >
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div
            className="w-16 h-16 rounded-xl flex-shrink-0 overflow-hidden"
            style={{ boxShadow: `0 4px 14px ${casino.color}40` }}
          >
            {casino.logo ? (
              <img src={casino.logo} alt={casino.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-black text-lg text-white" style={{ background: casino.color }}>
                {casino.initials}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-black text-gray-900 text-base leading-snug truncate" data-testid={`text-casino-name-${casino.id}`}>
                {casino.name}
              </h3>
            </div>
            <div className="flex items-center gap-1.5 mb-2">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span className="font-bold text-gray-900 text-sm" data-testid={`text-rating-${casino.id}`}>{casino.rating}</span>
              <span className="text-gray-400 text-xs">/ 10</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <span className="inline-flex items-center gap-1 bg-[#e8f0fe] text-[#003da5] text-xs font-semibold px-2.5 py-1 rounded-full" data-testid={`badge-license-${casino.id}`}>
                <Shield className="w-3 h-3" /> {casino.license}
              </span>
              {cryptoSupport && (
                <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-orange-200" data-testid={`badge-crypto-${casino.id}`}>
                  <Wallet className="w-3 h-3" /> Krypto
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-[#f3f6fb] rounded-xl p-3 text-center">
            <div className="font-black text-[#003da5] text-sm leading-tight" data-testid={`text-bonus-${casino.id}`}>{casino.bonus}</div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mt-0.5">Bonus</div>
          </div>
          <div className="bg-[#f3f6fb] rounded-xl p-3 text-center">
            <div className="font-black text-gray-800 text-sm leading-tight" data-testid={`text-freespins-${casino.id}`}>{casino.freeSpins}</div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mt-0.5">Free Spins</div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
          <span className="flex items-center gap-1"><Scale className="w-3 h-3" /> Wager: {casino.wager}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {casino.withdrawTime}</span>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Min. vklad: {casino.minDeposit} Kč
        </div>

        <div className="mt-3 border-t border-[#eef1f7] pt-3">
          <button
            onClick={() => setShowPayments(!showPayments)}
            className="w-full flex items-center justify-between text-gray-500 hover:text-[#003da5] transition-colors"
            data-testid={`button-payments-toggle-${casino.id}`}
          >
            <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold">
              <CreditCard className="w-3.5 h-3.5" /> Platební metody ({casino.paymentMethods.length})
            </span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${showPayments ? "rotate-180" : ""}`} />
          </button>
          {showPayments && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Suspense fallback={<div style={{ height: "40px" }} />}>
                {casino.paymentMethods.map(m => (
                  <PaymentLogoPill key={m} name={PAYMENT_METHOD_NAMES[m] || m} />
                ))}
              </Suspense>
            </div>
          )}
        </div>
      </div>

      <div className="px-5 pb-4 flex flex-col gap-2">
        <a href={casino.ctaUrl} target="_blank" rel="sponsored noopener noreferrer" aria-label={`Hrát nyní ${casino.name}`}>
          <Button
            className="hraj-btn w-full font-bold text-white gap-2"
            style={{ background: "#003da5" }}
            data-testid={`button-hraj-${casino.id}`}
          >
            Hrát nyní <ArrowRight className="hraj-arrow w-4 h-4" />
          </Button>
        </a>
        {casino.reviewUrl ? (
          <a
            href={casino.reviewUrl}
            className="flex items-center justify-center gap-1.5 py-2 text-[#003da5] text-sm font-semibold hover:text-[#002d7c] transition-colors"
            data-testid={`button-recenze-${casino.id}`}
          >
            <BookOpen className="w-3.5 h-3.5" /> Recenze
          </a>
        ) : (
          <span className="flex items-center justify-center gap-1.5 py-2 text-gray-400 text-sm font-semibold cursor-default" data-testid={`button-recenze-${casino.id}`}>
            <BookOpen className="w-3.5 h-3.5" /> Recenze brzy
          </span>
        )}
      </div>

      <div className="px-5 py-2.5 bg-[#f3f5f9] border-t border-[#dde4ee] flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-600">
          <Lock className="w-3 h-3 text-gray-500" /> 18+ | Hrajte zodpovědně
        </span>
        <span className="text-[11px] text-gray-500 font-semibold">Podmínky se vztahují</span>
      </div>
    </div>
  );
}

export default function KasinaPage() {
  useSEO({ title: "Nejlepší Online Kasina 2026 – Srovnání a recenze | HrajBonus", description: "Porovnejte 20 nejlepších online kasin v ČR. Hodnocení bonusů, her, výplat a bezpečnosti. Aktualizovaný žebříček od nezávislých expertů. 18+",
  url: "https://hrajbonus.com/kasina", });

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("rating");
  const [licenseFilter, setLicenseFilter] = useState<LicenseFilter>("all");
  const [cryptoFilter, setCryptoFilter] = useState(false);
  const [mobileFilter, setMobileFilter] = useState(false);
  const [depositFilter, setDepositFilter] = useState<DepositFilter>("all");

  useEffect(() => {
    const pageTitle = "Nejlepší online kasina 2026 | Kompletní průvodce | HrajBonus";
    const pageDescription = "Kompletní seznam nejlepších online kasin pro české hráče. Porovnejte bonusy, licence, platební metody a přečtěte si nezávislé recenze. Aktualizováno 2026.";
    const pageUrl = "https://hrajbonus.com/kasina";

    document.title = pageTitle;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("name", "description", pageDescription);
    setMeta("property", "og:title", pageTitle);
    setMeta("property", "og:description", pageDescription);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", pageUrl);
  }, []);

  const filteredCasinos = useMemo(() => {
    let result = [...CASINOS];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c => c.name.toLowerCase().includes(term));
    }

    if (licenseFilter !== "all") {
      result = result.filter(c => c.license === licenseFilter);
    }

    if (cryptoFilter) {
      result = result.filter(c => hasCrypto(c));
    }

    if (mobileFilter) {
      result = result.filter(c => c.mobileApp);
    }

    if (depositFilter !== "all") {
      const max = parseInt(depositFilter);
      result = result.filter(c => c.minDeposit <= max);
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case "rating": return b.rating - a.rating;
        case "bonusValue": return b.bonusValue - a.bonusValue;
        case "name": return a.name.localeCompare(b.name);
        default: return 0;
      }
    });

    return result;
  }, [searchTerm, sortBy, licenseFilter, cryptoFilter, mobileFilter, depositFilter]);

  const activeFilterCount = [
    licenseFilter !== "all",
    cryptoFilter,
    mobileFilter,
    depositFilter !== "all",
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#f5f7fb]">
      <Header />

      <section className="relative pt-16 pb-0 overflow-hidden">
        <div className="absolute inset-0 z-0" style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }} />
        <div className="absolute inset-0 z-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-14 sm:py-16 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-5">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-white/80 text-xs font-semibold uppercase tracking-widest">
              <Trophy className="w-3.5 h-3.5 text-[#f5c518]" /> Kompletní průvodce kasiny 2026
            </div>
            <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-white/70 text-xs font-semibold" data-testid="badge-aktualizovano-kasina">
              <Calendar className="w-3 h-3" /> Aktualizováno: 12. března 2026
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-4" data-testid="heading-kasina">
            Nejlepší online kasina pro české hráče
          </h1>
          <p className="text-white/75 text-lg leading-relaxed max-w-2xl mx-auto mb-6" data-testid="text-kasina-intro">
            Kompletní přehled {CASINOS.length} ověřených online kasin s nezávislými recenzemi, porovnáním bonusů a detailním hodnocením. Vyberte si kasino, které nejlépe vyhovuje vašim potřebám.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-8">
            <span className="flex items-center gap-2 text-white/70 text-sm">
              <Shield className="w-4 h-4 text-green-400" /> Pouze licencovaná kasina
            </span>
            <span className="flex items-center gap-2 text-white/70 text-sm">
              <Award className="w-4 h-4 text-amber-400" /> Nezávislé hodnocení
            </span>
            <span className="flex items-center gap-2 text-white/70 text-sm">
              <Clock className="w-4 h-4 text-blue-300" /> Pravidelně aktualizováno
            </span>
            <span className="flex items-center gap-2 text-white/70 text-sm">
              <BookOpen className="w-4 h-4 text-purple-300" /> Detailní recenze
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href="#casino-list">
              <Button size="default" className="hraj-btn font-bold gap-2" style={{ background: "#ffffff", color: "#003da5" }} data-testid="button-hero-kasina-list">
                Prohlédnout kasina <ArrowRight className="hraj-arrow w-4 h-4" />
              </Button>
            </a>
            <a href="#jak-vybrat">
              <Button size="default" variant="outline" className="hraj-btn border-white/30 text-white font-semibold" style={{ background: "rgba(255,255,255,0.08)" }} data-testid="button-hero-jak-vybrat">
                Jak vybrat kasino
              </Button>
            </a>
          </div>
        </div>
        <div className="relative z-10">
          <svg viewBox="0 0 1440 44" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none" style={{ display: "block", verticalAlign: "bottom", height: "44px" }}>
            <rect x="0" y="40" width="1440" height="4" fill="#f5f7fb" />
            <path d="M0 44L1440 44L1440 0C1200 35 720 40 0 0L0 44Z" fill="#f5f7fb" />
          </svg>
        </div>
      </section>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-10 space-y-12">

        <section id="casino-list" data-testid="section-casino-list">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-gray-900 mb-2" data-testid="heading-casino-list">Všechna online kasina</h2>
            <p className="text-gray-500 text-base">Prozkoumejte {CASINOS.length} ověřených kasin — filtrujte, řaďte a najděte to pravé pro vás</p>
          </div>

          <div className="bg-white rounded-2xl border border-[#dde4ee] p-5 shadow-sm mb-8" data-testid="casino-filters">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Hledat kasino podle názvu..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#003da5]/20 focus:border-[#003da5]"
                  data-testid="input-search-kasina"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Řadit:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="py-2.5 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#003da5]/20 focus:border-[#003da5] bg-white"
                  data-testid="select-sort-kasina"
                >
                  <option value="rating">Hodnocení</option>
                  <option value="bonusValue">Výše bonusu</option>
                  <option value="name">Název A–Z</option>
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mr-1">Filtr:</span>

              {(["all", "MGA", "Curaçao", "Anjouan"] as LicenseFilter[]).map(lic => (
                <button
                  key={lic}
                  onClick={() => setLicenseFilter(licenseFilter === lic ? "all" : lic)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    licenseFilter === lic
                      ? "bg-[#003da5] text-white border-[#003da5]"
                      : "bg-white text-gray-600 border-gray-200 hover:border-[#003da5]/40 hover:text-[#003da5]"
                  }`}
                  data-testid={`filter-license-${lic.toLowerCase()}`}
                >
                  {lic === "all" ? "Všechny licence" : lic}
                </button>
              ))}

              <div className="w-px h-5 bg-gray-200 mx-1" />

              <button
                onClick={() => setCryptoFilter(!cryptoFilter)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1 ${
                  cryptoFilter
                    ? "bg-[#003da5] text-white border-[#003da5]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-[#003da5]/40 hover:text-[#003da5]"
                }`}
                data-testid="filter-crypto"
              >
                <Wallet className="w-3 h-3" /> Krypto
              </button>

              <button
                onClick={() => setMobileFilter(!mobileFilter)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1 ${
                  mobileFilter
                    ? "bg-[#003da5] text-white border-[#003da5]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-[#003da5]/40 hover:text-[#003da5]"
                }`}
                data-testid="filter-mobile"
              >
                <Smartphone className="w-3 h-3" /> Mobilní app
              </button>

              <div className="w-px h-5 bg-gray-200 mx-1" />

              {(["all", "250", "500"] as DepositFilter[]).map(dep => (
                <button
                  key={dep}
                  onClick={() => setDepositFilter(depositFilter === dep ? "all" : dep)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    depositFilter === dep
                      ? "bg-[#003da5] text-white border-[#003da5]"
                      : "bg-white text-gray-600 border-gray-200 hover:border-[#003da5]/40 hover:text-[#003da5]"
                  }`}
                  data-testid={`filter-deposit-${dep}`}
                >
                  {dep === "all" ? "Jakýkoli vklad" : `Do ${dep} Kč`}
                </button>
              ))}
            </div>

            {(activeFilterCount > 0 || searchTerm) && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                <span className="text-sm text-gray-500" data-testid="text-results-count">
                  Nalezeno: <strong>{filteredCasinos.length}</strong> {filteredCasinos.length === 1 ? "kasino" : filteredCasinos.length < 5 ? "kasina" : "kasin"}
                </span>
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setLicenseFilter("all");
                    setCryptoFilter(false);
                    setMobileFilter(false);
                    setDepositFilter("all");
                    setSortBy("rating");
                  }}
                  className="text-xs text-[#003da5] hover:underline font-semibold"
                  data-testid="button-reset-filters"
                >
                  Resetovat filtry
                </button>
              </div>
            )}
          </div>

          {filteredCasinos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="casino-grid">
              {filteredCasinos.map(casino => (
                <CasinoCard key={casino.id} casino={casino} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-[#dde4ee]" data-testid="text-no-results">
              <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-semibold mb-1">Žádné kasino neodpovídá vašim filtrům</p>
              <p className="text-gray-400 text-sm">Zkuste upravit filtry nebo vyhledávání</p>
            </div>
          )}
        </section>

        <section id="jak-vybrat" data-testid="section-jak-vybrat">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-[#003da5]" /> Jak vybrat nejlepší online kasino
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Výběr správného online kasina je klíčový pro bezpečný a příjemný herní zážitek. Existuje několik důležitých faktorů, na které byste se měli zaměřit před registrací v jakémkoli kasinu. Naši redaktoři hodnotí každé kasino podle přísných kritérií, abyste měli jistotu, že doporučujeme pouze kvalitní platformy.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Prvním krokem je vždy ověření licence. Kasino s platnou licencí od uznávaného regulátora (jako MGA nebo Curaçao eGaming) musí splňovat přísné požadavky na bezpečnost, férovost her a ochranu prostředků hráčů. Dále se zaměřte na nabídku her, podmínky bonusů a dostupné platební metody — ideálně takové, které znáte a kterým důvěřujete.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Nezapomínejte také na zákaznickou podporu. Kvalitní kasino nabízí live chat dostupný 24/7, rychlé reakce a profesionální přístup k řešení problémů. V našich <a href="/redakcni-metodologie" className="text-[#003da5] font-semibold hover:underline">redakčních recenzích</a> detailně hodnotíme všechny tyto aspekty.
            </p>
          </div>
        </section>

        <section data-testid="section-bezpecnost">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#003da5]" /> Bezpečnost a licence online kasin
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Bezpečnost by měla být vaší nejvyšší prioritou při výběru online kasina. Licencovaná kasina používají nejmodernější šifrovací technologie (SSL/TLS) k ochraně vašich osobních údajů a finančních transakcí. Každé kasino v našem seznamu má platnou licenci od uznávaného mezinárodního regulátora.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Mezi nejuznávanější licence patří Malta Gaming Authority (MGA), která vyžaduje pravidelné audity a oddělené účty pro prostředky hráčů. Curaçao eGaming je nejrozšířenější licence pro mezinárodní kasina, zatímco Anjouan Gaming Commission je novější jurisdikce nabízející základní ochranu. Vždy si ověřte platnost licence přímo na webu regulátora.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { name: "MGA", level: "Vysoká ochrana", color: "text-green-700", bg: "bg-green-50", border: "border-green-200" },
                { name: "Curaçao eGaming", level: "Střední ochrana", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
                { name: "Anjouan Gaming", level: "Základní ochrana", color: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200" },
              ].map(lic => (
                <div key={lic.name} className={`${lic.bg} ${lic.border} border rounded-xl p-4`}>
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{lic.name}</h3>
                  <span className={`text-xs font-semibold ${lic.color}`}>{lic.level}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="section-bonusy">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-[#003da5]" /> Bonusy a podmínky
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Kasinové bonusy jsou skvělým způsobem, jak začít hrát s větším kapitálem. Většina kasin nabízí uvítací bonus při prvním vkladu, který může dosáhnout až stovek tisíc korun. K bonusu často dostanete také free spiny na vybrané automaty.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Klíčovým faktorem u každého bonusu je wagering (požadavek na protočení). Toto číslo udává, kolikrát musíte bonusovou částku protočit, než si můžete vybrat výhry. V našem seznamu najdete u každého kasina wagering podmínky — většina se pohybuje mezi 30× a 35×.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Před aktivací jakéhokoli bonusu si vždy přečtěte kompletní <a href="/bonusy" className="text-[#003da5] font-semibold hover:underline">podmínky bonusů</a>. Důležité je sledovat maximální sázku při protáčení, časový limit pro splnění podmínek a hry, které do protáčení nepočítají nebo se počítají sníženou sazbou.
            </p>
          </div>
        </section>

        <section data-testid="section-platby">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-[#003da5]" /> Platební metody v online kasinech
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Moderní online kasina nabízejí širokou škálu platebních metod, aby vyhověla preferencím všech hráčů. Od tradičních platebních karet přes e-peněženky až po kryptoměny — každá metoda má své výhody a nevýhody z hlediska rychlosti, poplatků a soukromí.
            </p>
            <p className="text-gray-600 leading-relaxed mb-5">
              Pro české hráče jsou nejoblíbenější metody Visa a Mastercard pro jednoduchost, Skrill a Neteller pro rychlé výběry, a Bitcoin pro ty, kteří preferují anonymitu. Minimální vklad se u většiny kasin pohybuje od 250 Kč do 500 Kč.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { cat: "Platební karty", methods: "Visa, Mastercard", note: "Okamžité vklady, výběry 2–5 dní" },
                { cat: "E-peněženky", methods: "Skrill, Neteller, MiFinity", note: "Rychlé výběry do 24 hodin" },
                { cat: "Kryptoměny", methods: "Bitcoin, Ethereum, Litecoin", note: "Anonymní, bez poplatků" },
                { cat: "Předplacené karty", methods: "Paysafecard, Neosurf", note: "Vklady bez bankovních údajů" },
              ].map(pm => (
                <div key={pm.cat} className="bg-[#f3f6fb] rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{pm.cat}</h3>
                  <p className="text-gray-700 text-xs mb-2">{pm.methods}</p>
                  <p className="text-gray-500 text-xs italic">{pm.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="section-mobilni">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <Smartphone className="w-6 h-6 text-[#003da5]" /> Mobilní hraní
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Většina moderních online kasin je plně optimalizována pro mobilní zařízení. Hráči mohou přistupovat ke svým oblíbeným hrám přímo z webového prohlížeče na telefonu nebo tabletu bez nutnosti stahování jakékoli aplikace. Responzivní design zajišťuje plynulý herní zážitek na jakékoli velikosti obrazovky.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Některá kasina jdou ještě dál a nabízejí dedikované mobilní aplikace pro Android a iOS. Tyto aplikace často nabízejí lepší výkon, push notifikace o nových promocích a optimalizované rozhraní pro dotykové ovládání. V našem přehledu jasně označujeme kasina s mobilní aplikací.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Chcete vědět více? Podívejte se na náš kompletní průvodce <a href="/kasina/mobilni" className="text-[#003da5] font-semibold hover:underline">mobilními kasiny</a>, kde najdete detailní porovnání a tipy pro mobilní hráče.
            </p>
          </div>
        </section>

        <section data-testid="section-vybery">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <Wallet className="w-6 h-6 text-[#003da5]" /> Výběry a limity
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Rychlost a spolehlivost výběrů je jedním z nejdůležitějších faktorů při hodnocení online kasina. Nejrychlejší výběry nabízejí e-peněženky a kryptoměny — často do 24 hodin. Platební karty a bankovní převody obvykle trvají 2–5 pracovních dní.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Při prvním výběru většina kasin vyžaduje KYC (Know Your Customer) verifikaci, která zahrnuje ověření identity kopií občanského průkazu, dokladem o adrese a případně potvrzením platební metody. Po úspěšné verifikaci jsou další výběry zpracovány rychleji. Denní a měsíční limity se liší podle kasina — u VIP hráčů jsou obvykle vyšší.
            </p>
          </div>
        </section>

        <section data-testid="section-zodpovedne">
          <div className="bg-[#fef9ec] border border-amber-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-amber-600" /> Zodpovědné hraní
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Hazardní hry by měly být vždy formou zábavy, nikoli způsobem výdělku. Stanovte si jasný rozpočet, kolik jste ochotni prohrát, a nikdy tento limit nepřekračujte. Pokud zjistíte, že hrajete více, než jste plánovali, nebo že hraní negativně ovlivňuje váš každodenní život, je čas požádat o pomoc.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Většina kasin v našem seznamu nabízí nástroje pro zodpovědné hraní: limity vkladů, limity proher, časové limity herních sesí a možnost dočasného nebo trvalého sebeomezení. Tyto nástroje vám pomohou udržet kontrolu nad svým hraním.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="https://www.gamcare.org.uk" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-white px-3 py-2 rounded-lg border border-amber-200 text-sm font-semibold text-gray-700 hover:text-[#003da5] transition-colors" data-testid="link-gamcare">
                GamCare
              </a>
              <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-white px-3 py-2 rounded-lg border border-amber-200 text-sm font-semibold text-gray-700 hover:text-[#003da5] transition-colors" data-testid="link-begambleaware">
                BeGambleAware
              </a>
              <a href="https://www.gamblingtherapy.org" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-white px-3 py-2 rounded-lg border border-amber-200 text-sm font-semibold text-gray-700 hover:text-[#003da5] transition-colors" data-testid="link-gamblingtherapy">
                Gambling Therapy
              </a>
            </div>
          </div>
        </section>

        <section data-testid="section-faq-kasina">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-gray-900 mb-2">Často kladené otázky</h2>
            <p className="text-gray-500 text-base">Odpovědi na nejčastější dotazy o online kasinech</p>
          </div>
          <div className="flex flex-col gap-2" data-testid="faq-kasina-accordion">
            {FAQ_ITEMS.map((faq, i) => (
              <FaqItem key={i} q={faq.q} a={faq.a} index={i} />
            ))}
          </div>
        </section>

        <section data-testid="section-related-links">
          <div className="bg-[#f0f5ff] border border-[#003da5]/15 rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-black text-gray-900 mb-4">Prozkoumat dále</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: "Zahraniční kasina", href: "/kasina/zahranicni", icon: Globe },
                { label: "Mobilní kasina", href: "/kasina/mobilni", icon: Smartphone },
                { label: "Bonusy", href: "/bonusy", icon: Trophy },
                { label: "Automaty", href: "/automaty", icon: Gamepad2 },
                { label: "Porovnat kasina", href: "/porovnat", icon: Scale },
                { label: "Redakční metodologie", href: "/redakcni-metodologie", icon: BookOpen },
              ].map(({ label, href, icon: Icon }) => (
                <a
                  key={href}
                  href={href}
                  className="flex flex-col items-center gap-2 bg-white rounded-xl p-4 border border-[#dde4ee] hover:border-[#003da5]/40 hover:shadow-md transition-all text-center group"
                  data-testid={`related-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon className="w-6 h-6 text-[#003da5] group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-semibold text-gray-700 group-hover:text-[#003da5] transition-colors">{label}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

      </div>

      <Footer />
    </div>
  );
}
